import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, Avatar, IconButton, Paper, Chip, Dialog, DialogContent } from '@mui/material';
import { Row, Col } from 'react-bootstrap';
import { FileUploadInput } from '../../../components/forms';
import { Send, Image as ImageIcon, AttachFile, Close, PlayCircle } from '@mui/icons-material';
import { useAppSelector } from '../../../hooks/useAuth';
import { convertTimestampToIST } from '../../../utils/formatters';

type TicketDetail = {
  supportticketdetailid: number;
  supportticketid: number;
  assignee: number;
  statusid: number;
  note: string;
  medias: string[];
  userid: number;
  enteredon: string;
};

interface CommentsSectionProps {
  addNote: string;
  saving: boolean;
  onChangeNote: (value: string) => void;
  onPostComment: () => void;
  ticketDetailsList?: TicketDetail[];
  medias?: string[];
  uploading?: boolean;
  onImageUpload?: (field: string, file: File) => Promise<void>;
  onDeleteMedia?: (index: number) => void;
  resolveSrc?: (value: string) => string;
  currentUserName?: string;
  currentUserId?: number;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  addNote,
  saving,
  onChangeNote,
  onPostComment,
  ticketDetailsList = [],
  medias = [],
  uploading = false,
  onImageUpload,
  onDeleteMedia,
  resolveSrc,
  currentUserName = 'User',
  currentUserId,
}) => {
  const user = useAppSelector((state) => state.auth.user);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{ src: string; type: 'image' | 'video' } | null>(null);

  // Check if file is a video
  const isVideoFile = (filename: string): boolean => {
    if (!filename) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.flv', '.wmv'];
    const lowerFilename = filename.toLowerCase();
    return videoExtensions.some(ext => lowerFilename.endsWith(ext));
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticketDetailsList]);

  // Filter out messages with both empty note and empty medias
  const filteredMessages = ticketDetailsList.filter((message) => {
    const hasNote = message.note && message.note.trim().length > 0;
    const hasMedia = Array.isArray(message.medias) && message.medias.length > 0;
    return hasNote || hasMedia;
  });

  // Sort messages by timestamp
  const sortedMessages = [...filteredMessages].sort((a, b) => 
    new Date(a.enteredon).getTime() - new Date(b.enteredon).getTime()
  );

  // Determine if message is from admin or user
  const isAdminMessage = (message: TicketDetail) => {
    // If userid equals assignee, it's an admin message
    // Also check if the current user is the one who sent the message
    return message.userid === message.assignee || (currentUserId && message.userid === currentUserId);
  };

  // Get message sender name
  const getMessageSenderName = (message: TicketDetail) => {
    return isAdminMessage(message) ? 'Admin' : currentUserName;
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = convertTimestampToIST(timestamp);
    
    if (!date) return '';
    
    // Format: "Dec 4, 2025 11:20 AM"
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Format time in 12-hour format
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    
    return `${month} ${day}, ${year} ${hours}:${minutesStr} ${ampm}`;
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Chat Messages Container */}
      <Box
        sx={{
          height: '500px',
          overflowY: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          p: 2,
          mb: 3,
          backgroundColor: '#fafafa',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
        }}
      >
        {sortedMessages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#666',
            }}
          >
            <Typography variant="body2">No messages yet. Start the conversation!</Typography>
          </Box>
        ) : (
          sortedMessages.map((message, index) => {
            const isAdmin = isAdminMessage(message);
            const senderName = getMessageSenderName(message);
            
            return (
              <Box
                key={message.supportticketdetailid}
                sx={{
                  display: 'flex',
                  justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    maxWidth: { xs: '85%', sm: '70%', md: '60%' },
                    minWidth: { xs: '200px', sm: '250px', md: '300px' },
                    display: 'flex',
                    flexDirection: isAdmin ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      bgcolor: isAdmin ? '#1976d2' : '#4caf50',
                      width: 32,
                      height: 32,
                      fontSize: '0.875rem',
                    }}
                  >
                    {senderName.charAt(0).toUpperCase()}
                  </Avatar>

                  {/* Message Content */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isAdmin ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {/* Message Bubble */}
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        backgroundColor: isAdmin ? '#1976d2' : '#ffffff',
                        color: isAdmin ? '#ffffff' : '#333333',
                        borderRadius: isAdmin 
                          ? '18px 18px 4px 18px' 
                          : '18px 18px 18px 4px',
                        border: isAdmin ? 'none' : '1px solid #e0e0e0',
                        maxWidth: '100%',
                        minWidth: 'fit-content',
                        wordWrap: 'break-word',
                        width: '100%',
                      }}
                    >
                      {/* Sender Name */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          opacity: 0.8,
                          fontSize: '0.75rem',
                        }}
                      >
                        {senderName}
                      </Typography>

                      {/* Message Text */}
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.5,
                          mb: message.medias && message.medias.length > 0 ? 1 : 0,
                          minHeight: '1.2em',
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {message.note}
                      </Typography>

                      {/* Media Attachments */}
                      {Array.isArray(message.medias) && message.medias.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Row className="g-2">
                            {message.medias.map((media, mediaIndex) => {
                              const mediaSrc = resolveSrc ? resolveSrc(media) : media;
                              const isVideo = isVideoFile(media);
                              return (
                                <Col key={`message-media-${message.supportticketdetailid}-${mediaIndex}`} xs={12} sm={message.medias.length === 1 ? 12 : 6} md={message.medias.length === 1 ? 8 : 6}>
                                  <Box
                                    sx={{
                                      border: `1px solid ${isAdmin ? 'rgba(255,255,255,0.3)' : '#e0e0e0'}`,
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      height: { xs: 200, sm: 250, md: 300 },
                                      backgroundColor: isAdmin ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s ease-in-out',
                                      position: 'relative',
                                      '&:hover': {
                                        transform: 'scale(1.02)',
                                      },
                                    }}
                                    onClick={() => {
                                      setSelectedMedia({ src: mediaSrc, type: isVideo ? 'video' : 'image' });
                                    }}
                                  >
                                    {isVideo ? (
                                      <>
                                        <video
                                          src={mediaSrc}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                          }}
                                          controls={false}
                                          muted
                                          onError={(e) => {
                                            (e.target as HTMLVideoElement).style.display = 'none';
                                          }}
                                        />
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 60,
                                            height: 60,
                                            borderRadius: '50%',
                                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                            backdropFilter: 'blur(4px)',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                              transform: 'translate(-50%, -50%) scale(1.1)',
                                            },
                                          }}
                                        >
                                          <PlayCircle
                                            sx={{
                                              color: 'white',
                                              fontSize: 40,
                                              ml: 0.5,
                                            }}
                                          />
                                        </Box>
                                      </>
                                    ) : (
                                      <img
                                        src={mediaSrc}
                                        alt={`media-${mediaIndex}`}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                        }}
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Col>
                              );
                            })}
                          </Row>
                        </Box>
                      )}
                    </Paper>

                    {/* Timestamp */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#666',
                        fontSize: '0.7rem',
                        mt: 0.5,
                        px: 1,
                      }}
                    >
                      {formatTimestamp(message.enteredon)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message Input Section */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
        }}
      >
        {/* Selected Media Preview */}
        {medias.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#444', fontSize: '0.8rem' }}>
              Attachments ({medias.length})
            </Typography>
            <Row className="g-2">
              {medias.map((media, index) => {
                const mediaSrc = resolveSrc ? resolveSrc(media) : media;
                const isVideo = isVideoFile(media);
                return (
                  <Col key={index} xs={12} sm={6} md={4}>
                    <Box
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        height: { xs: 120, sm: 150, md: 180 },
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.02)',
                        },
                      }}
                      onClick={() => {
                        setSelectedMedia({ src: mediaSrc, type: isVideo ? 'video' : 'image' });
                      }}
                    >
                      {isVideo ? (
                        <>
                          <video
                            src={mediaSrc}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            controls={false}
                            muted
                            onError={(e) => {
                              (e.target as HTMLVideoElement).style.display = 'none';
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 50,
                              height: 50,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              backdropFilter: 'blur(4px)',
                              cursor: 'pointer',
                            }}
                          >
                            <PlayCircle
                              sx={{
                                color: 'white',
                                fontSize: 30,
                                ml: 0.5,
                              }}
                            />
                          </Box>
                        </>
                      ) : (
                        <img
                          src={mediaSrc}
                          alt={`media-${index}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                      {onDeleteMedia && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteMedia(index);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            width: 24,
                            height: 24,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Col>
                );
              })}
            </Row>
          </Box>
        )}

        {/* Input Area */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type a message..."
            value={addNote}
            onChange={(e) => onChangeNote(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fafafa',
                '& fieldset': { borderColor: '#ddd' },
                '&:hover fieldset': { borderColor: '#bdbdbd' },
                '&.Mui-focused fieldset': { borderColor: '#1976d2' },
              },
            }}
          />

          {/* Media Upload Button */}
          <IconButton
            size="small"
            onClick={() => setShowMediaUpload(!showMediaUpload)}
            sx={{
              color: '#666',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
          >
            <AttachFile />
          </IconButton>

          {/* Send Button */}
          <Button
            variant="contained"
            size="small"
            onClick={onPostComment}
            disabled={saving || (!addNote.trim() && medias.length === 0)}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' },
              '&:disabled': { backgroundColor: 'rgba(0,0,0,0.12)' },
            }}
          >
            <Send fontSize="small" />
          </Button>
        </Box>

        {/* Media Upload Section */}
        {showMediaUpload && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <FileUploadInput
              field="medias"
              formData={{}}
              onChange={() => {}}
              label="Upload Media"
              folderPath="support"
              folderType="media"
              onImageUpload={onImageUpload}
              uploading={uploading}
              hideLabel={false}
              accept="image/*,video/mp4"
            />
          </Box>
        )}
      </Paper>

      {/* Media Preview Dialog */}
      <Dialog
        open={!!selectedMedia}
        onClose={() => setSelectedMedia(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            borderRadius: 2,
          }
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 2 }}>
          <IconButton
            onClick={() => setSelectedMedia(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
          >
            <Close />
          </IconButton>
          {selectedMedia && (
            <>
              {selectedMedia.type === 'video' ? (
                <video
                  src={selectedMedia.src}
                  controls
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '85vh',
                    display: 'block',
                  }}
                />
              ) : (
                <img
                  src={selectedMedia.src}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '85vh',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CommentsSection;
